import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    CircularProgress,
    Container,
    Grid,
} from '@mui/material'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useCFDContext } from 'pages/CustomerFacingDisplayPage/context/useCFDContext'
import { CFDSubjects } from '../../views/CustomerFacingDisplay/hardwareAgent/CFDSubjects'
import { CFDClientEvent } from '../../views/CustomerFacingDisplay/hardwareAgent/CFDClientEvent'
import { useHistory } from 'react-router-dom'
import { CFDROUTE } from 'pages/CustomerFacingDisplayPage/CFDLayout'
import CodeInput from 'views/CustomerFacingDisplay/components/CodeInput'
import crypto from 'crypto-es'
import useValidateConnection from './hook/useValidateConnection'
import cfdToast from 'views/CustomerFacingDisplay/utils/cfdToast'
import cookies from 'utilities/cookie'
import { COOKIES_TOKEN } from 'views/CustomerFacingDisplay/utils/localStorageKey'
let initTokenValidate = false
const token = cookies.get(COOKIES_TOKEN)

interface CodeForm {
    code: string
}
const useTimer = (timeToCount: number) => {
    const [secondToGo, setSecondToGo] = useState<number>(timeToCount)

    const timerRef = useRef<any>()
    const onRestart = useCallback((timeToCount: number) => {
        setSecondToGo(timeToCount)
    }, [])

    useEffect(() => {
        if (secondToGo > 0)
            timerRef.current = setInterval(() => {
                setSecondToGo((prev) => prev - 1)
            }, 1000)

        return () => {
            clearInterval(timerRef.current)
        }
    }, [secondToGo])

    if (secondToGo < 0) {
        clearInterval(timerRef.current)
    }

    return {
        secondToGo,
        onRestart,
    }
}

const WAITING_RESPONSE_TIME = 3
const CFDCodeConnectorPage = () => {
    const [tokenValidating, setTokenValidating] = useState(false)
    const [useToken, setUseToken] = useState(false)
    const {
        connectWithPOSUsingCode,
        onReceiveConnected,
        clientID,
        connectWithPOSUsingToken,
        isManualConnect,
        isHWOnline,
    } = useCFDContext()
    const history = useHistory()
    const { secondToGo, onRestart } = useTimer(0)

    const validateSchema: yup.SchemaOf<CodeForm> = yup
        .object()
        .shape({
            code: yup.string().required(),
        })
        .defined()

    const form = useForm<CodeForm>({
        defaultValues: {
            code: '',
        },
        resolver: yupResolver(validateSchema),
    })
    const { isTokenValid } = useValidateConnection(false)
    const validateToken = useCallback(async () => {
        try {
            if (!isHWOnline) return
            initTokenValidate = true
            if (!(await isTokenValid())) return
            setUseToken(true)
            setTokenValidating(true)
            const hashedToken = crypto.SHA256(token).toString()

            await connectWithPOSUsingToken(hashedToken, () => {
                setTokenValidating(false)
            })
            cfdToast.info('Token is validating...')
        } catch (error) {
            setTokenValidating(false)
            setUseToken(false)
            cfdToast.error('Unable to validate token.')
        }
    }, [connectWithPOSUsingToken, isHWOnline, isTokenValid])

    const onSubmit = useCallback(
        async (form: CodeForm) => {
            onRestart(WAITING_RESPONSE_TIME)
            await connectWithPOSUsingCode(form.code)
        },
        [connectWithPOSUsingCode, onRestart],
    )
    const isAllowSendCode = useMemo(() => secondToGo <= 0, [secondToGo])
    const alert = () => {
        if (!isHWOnline)
            return (
                <Alert severity="error">
                    Hardware agent is offline. Please check the connection
                </Alert>
            )
    }
    useEffect(() => {
        if (!initTokenValidate && !isManualConnect) validateToken()
    }, [isManualConnect, tokenValidating, validateToken])

    useEffect(() => {
        const subscription = CFDSubjects.subscribe((event) => {
            if (event.type === CFDClientEvent.OnReceiveMessage) {
                onReceiveConnected(event.params)
            }
        })

        return () => {
            subscription.unsubscribe()
        }
    }, [onReceiveConnected])

    useEffect(() => {
        if (clientID) history.push(CFDROUTE.HOME)
    }, [clientID, history])

    return (
        <Container maxWidth={'xl'}>
            <Card
                sx={{
                    margin: 'auto',
                    width: '100%',
                    maxWidth: 500,
                    padding: '10px',
                    marginTop: '20%',
                }}
            >
                <CardHeader title="Connect to POS"> </CardHeader>
                {useToken ? (
                    <>
                        <CardContent>
                            <Grid
                                container
                                spacing={2}
                                alignItems={'center'}
                                justifyItems={'center'}
                                alignContent={'center'}
                                justifyContent={'space-between'}
                            >
                                <Grid item xs={12} sx={{ textAlign: 'center' }}>
                                    {alert()}
                                </Grid>
                                {tokenValidating && (
                                    <>
                                        <Grid
                                            item
                                            xs={12}
                                            sx={{ textAlign: 'center' }}
                                        >
                                            <CircularProgress />
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            sx={{ textAlign: 'center' }}
                                        >
                                            Token is validating...
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </CardContent>
                        <CardActions>
                            <Button
                                onClick={() => setUseToken(false)}
                                variant="outlined"
                                color="primary"
                                sx={{ mt: 2 }}
                            >
                                connect with code
                            </Button>
                            <Box display={'flex'} flexGrow={1} />
                            <Button
                                onClick={validateToken}
                                color="primary"
                                sx={{ mt: 2 }}
                            >
                                Retry
                            </Button>
                        </CardActions>
                    </>
                ) : (
                    <CardContent>
                        <Grid item>{alert()}</Grid>
                        <Box
                            p={5}
                            sx={{
                                overflow: 'auto',
                            }}
                        >
                            <Grid container direction={'column'} spacing={2}>
                                <Grid item>
                                    <CodeInput
                                        disabled={!isAllowSendCode}
                                        length={6}
                                        onComplete={(e) => {
                                            form.setValue('code', e)
                                        }}
                                    />
                                </Grid>
                                <Grid item>
                                    <Button
                                        fullWidth
                                        disabled={!isAllowSendCode}
                                        color="primary"
                                        onClick={form.handleSubmit(onSubmit)}
                                    >
                                        {isAllowSendCode ? (
                                            'Input Code'
                                        ) : (
                                            <>
                                                Available retry in {secondToGo}s
                                                ...{' '}
                                            </>
                                        )}
                                    </Button>
                                </Grid>
                                {token && (
                                    <Grid item>
                                        <Button
                                            fullWidth
                                            onClick={validateToken}
                                            variant="outlined"
                                            color="primary"
                                        >
                                            connect with Token
                                        </Button>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </CardContent>
                )}
            </Card>
        </Container>
    )
}

export default CFDCodeConnectorPage
